import { PROVIDERS, isUnlimited, type AgentProvider, type ProjectHealth } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Activity, Archive, ChevronLeft, FolderUp, HardDriveDownload, KeyRound, Pause, Play, RefreshCw, RotateCcw, Settings2, Terminal, Timer, Trash2, Users } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { accountsApi } from "@/api/accounts.ts";
import { googleApi } from "@/api/imports.ts";
import { projectsApi } from "@/api/projects.ts";
import { cn } from "@/lib/cn.ts";
import { cpuShare } from "@/lib/cpu.ts";
import { Alert, Button, Field, Input, Select, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { ImportWizard, type ImportSource } from "./ImportWizard.tsx";
import { projectQuery, useProjectActions } from "./ProjectPage.tsx";
import { ProjectMembers } from "./ProjectMembers.tsx";
import { ProjectSecrets } from "./ProjectSecrets.tsx";
import { SnapshotsCard } from "./SnapshotsCard.tsx";
import { StateBadge } from "./StateBadge.tsx";

const SECTIONS = ["overview", "settings", "secrets", "sharing"] as const;
type SectionId = (typeof SECTIONS)[number];

const fmtMb = (mb: number | null | undefined) => (mb === null || mb === undefined ? "—" : mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`);

/**
 * Project settings laid out like the original Codvero: one scrolling page with anchor tabs
 * (Overview / Settings / Secrets / Sharing), a health center, identity and fact tiles, then the forms.
 */
export function ProjectSettingsPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const p = useQuery(projectQuery(projectId)).data;
  const running = p?.state === "running";
  const health = useQuery({ queryKey: ["projects", projectId, "health"], queryFn: () => projectsApi.health(projectId), refetchInterval: running ? 5000 : false, enabled: !!p });
  const actions = useProjectActions(projectId);
  const [active, setActive] = useState<SectionId>("overview");
  const scrollerRef = useRef<HTMLDivElement>(null);
  const canEdit = !!p && p.myRole !== "viewer";
  const isOwner = p?.myRole === "owner";

  // Highlight the section in view (the original uses anchor links; we track scroll position too).
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (vis) setActive(vis.target.id.replace("project-", "") as SectionId);
      },
      { root: null, rootMargin: "-20% 0px -65% 0px" },
    );
    for (const id of SECTIONS) {
      const el = root.querySelector(`#project-${id}`);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [p?.id]);
  const go = (id: SectionId) => {
    setActive(id);
    scrollerRef.current?.querySelector(`#project-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!p) return <div className="grid h-full place-items-center"><Spinner /></div>;
  const h = health.data;
  const busy = actions.start.isPending || actions.stop.isPending || actions.restart.isPending || actions.recreate.isPending || p.state === "creating";
  const err = actions.start.error ?? actions.stop.error ?? actions.restart.error ?? actions.recreate.error;
  const rel = (iso: string | null) => (iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)) : "—");

  return (
    <div ref={scrollerRef} className="-mx-4 -mt-3 overflow-x-clip">
      <div className="sticky -top-3 z-10 border-b border-border bg-bg/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 pt-3">
          <Link to="/projects/$projectId" params={{ projectId }} className="grid size-8 place-items-center rounded-full border border-border text-fg-muted hover:text-fg" aria-label={t("common.back")}><ChevronLeft className="size-4 rtl:-scale-x-100" /></Link>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-fg-muted">{t("projects.title")}</div>
            <h1 className="truncate text-base font-semibold">{t("projectSettings.title", { name: p.name })}</h1>
          </div>
          <button type="button" aria-label={t("git.refresh")} className="grid size-8 place-items-center rounded-full border border-border text-fg-muted hover:text-fg" onClick={() => { void health.refetch(); void qc.invalidateQueries({ queryKey: ["projects", projectId] }); }}><RefreshCw className={cn("size-4", health.isFetching && "animate-spin")} /></button>
        </div>
        <nav className="mx-auto flex max-w-5xl gap-1 px-4 pt-2 pb-2">
          {SECTIONS.map((id) => (
            <button key={id} type="button" onClick={() => go(id)} className={cn("rounded-md px-3 py-1 text-xs", active === id ? "bg-surface-2 font-medium text-fg" : "text-fg-muted hover:text-fg")}>{t(`projectSettings.sections.${id}`)}</button>
          ))}
        </nav>
      </div>

      <div className="mx-auto max-w-5xl space-y-10 px-4 py-6">
        {/* ------------------------------------------------ OVERVIEW */}
        <Section id="overview" label={t("projectSettings.sections.overview")} desc={t("projectSettings.overviewDesc")}>
          <Panel icon={Activity} title={t("health.title")} badge={<StateBadge state={p.state} />} aside={t("projectSettings.updatesEvery")} sub={t("projectSettings.healthSub")}>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <Tile label={t("projectSettings.cpuNow")} value={h?.container.cpuPercent === null || h?.container.cpuPercent === undefined ? (running ? t("projectSettings.calculating") : "—") : ((c) => (c && c.cores ? `${c.pct}% · ${t("machines.coresOf", { used: c.used, cores: c.cores })}` : `${h.container.cpuPercent}%`))(cpuShare(h.container.cpuPercent, h.cpuCores))} />
              <Tile label={t("projectSettings.memNow")} value={h ? `${fmtMb(h.container.memoryUsedMb)} / ${fmtMb(h.container.memoryLimitMb ?? h.limits.memoryMb)}` : "—"} />
              <Tile label={t("projectSettings.workspaceDisk")} value={h?.disks[0] ? `${fmtMb(h.disks[0].usedMb)} / ${fmtMb(h.disks[0].totalMb)}` : "—"} />
              <Tile label={t("projectSettings.ipv4")} value={h?.network.ip ?? "—"} mono />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-xs">
              <span className="text-fg-muted">{t("projectSettings.activePorts")}</span>
              {h && h.network.listeningPorts.length > 0 ? h.network.listeningPorts.map((port) => <span key={port} dir="ltr" className="rounded border border-accent/40 bg-accent-soft/40 px-1.5 font-mono text-accent">:{port}</span>) : <span className="text-fg-muted">{t("projectSettings.noPorts")}</span>}
            </div>
            {err && <Alert tone="danger" className="mt-2">{toMsg(err)}</Alert>}
            {canEdit && (
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button className="h-10" disabled={busy || running} loading={actions.start.isPending} onClick={() => actions.start.mutate()}><Play className="size-4" /> {t("projectSettings.startProject")}</Button>
                <Button className="h-10" variant="secondary" disabled={busy || !running} loading={actions.stop.isPending} onClick={() => actions.stop.mutate()}><Pause className="size-4" /> {t("projectSettings.stopProject")}</Button>
                <Button className="h-10 border-warn/50 text-warn hover:bg-warn-soft sm:col-span-2" variant="ghost" disabled={busy} loading={actions.recreate.isPending} onClick={() => actions.recreate.mutate()} title={t("projectSettings.forceRestartHint")}><RotateCcw className="size-4" /> {t("projectSettings.forceRestart")}</Button>
              </div>
            )}
          </Panel>

          <Panel icon={Settings2} title={p.name} badge={<StateBadge state={p.state} />} aside={h ? `${t("projectSettings.refreshed")} ${rel(h.checkedAt)}` : ""} sub={<span dir="ltr">{p.slug}</span>}>
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <Chip>{t("projectSettings.source")}: {t("create.blank")}</Chip>
              {p.description && <Chip>{p.description}</Chip>}
              <Chip>{t("projectSettings.owner")}: {p.myRole}</Chip>
            </div>
          </Panel>

          <Group title={t("projectSettings.sections.overview")}>
            <KV k={t("projectSettings.container")} v={`cdv-${p.slug}`} mono />
            <KV k={t("health.state")} v={t(`projects.state.${p.state}`)} />
            <KV k="ID" v={h?.container.containerId?.slice(0, 12) ?? "—"} mono />
            <KV k={t("health.pids")} v={h?.container.pids ?? "—"} />
            <KV k={t("health.image")} v={h?.container.image ?? p.image} mono />
            <KV k={t("health.uptime")} v={h?.uptimeSec ? `${Math.floor(h.uptimeSec / 3600)}h ${Math.floor((h.uptimeSec % 3600) / 60)}m` : "—"} />
            <KV k={t("projectSettings.created")} v={rel(p.createdAt)} />
            <KV k={t("projectSettings.lastUsed")} v={rel(p.updatedAt)} />
          </Group>

          <ToolingGroups health={h} />
          <AuthFiles health={h} projectAccountId={p.agentAccountId} />
        </Section>

        {/* ------------------------------------------------ SETTINGS */}
        <Section id="settings" label={t("projectSettings.sections.settings")} desc={t("projectSettings.settingsDesc")}>
          <Panel icon={Archive} title={t("snapshots.title")} sub={t("snapshots.intro")}>
            <SnapshotsCard projectId={projectId} canEdit={canEdit} running={running} bare />
          </Panel>
          <LimitsPanel projectId={projectId} />
          {p.myRole === "owner" && p.kind === "project" && <DevModePanel projectId={projectId} />}
          <AccountPanel projectId={projectId} />
          {canEdit && <ImportPanel projectId={projectId} running={running} />}
          {canEdit && <DrivePanel projectId={projectId} state={p.state} />}
          <Panel icon={Timer} title={t("projectSettings.lifecycle")} sub={`${t("projectSettings.currentStatus")}: ${t(`projects.state.${p.state}`)}`}>
            {canEdit && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button className="h-10" disabled={busy || running} onClick={() => actions.start.mutate()}><Play className="size-4" /> {t("projectSettings.startProject")}</Button>
                <Button className="h-10" variant="secondary" disabled={busy || !running} onClick={() => actions.stop.mutate()}><Pause className="size-4" /> {t("projectSettings.stopProject")}</Button>
                <Button className="h-10 border-warn/50 text-warn hover:bg-warn-soft sm:col-span-2" variant="ghost" disabled={busy} onClick={() => actions.recreate.mutate()}><RotateCcw className="size-4" /> {t("projectSettings.forceRestart")}</Button>
                {isOwner && <DeleteButton projectId={projectId} name={p.name} onDeleted={() => { void qc.invalidateQueries({ queryKey: ["projects"] }); void nav({ to: "/" }); }} />}
              </div>
            )}
          </Panel>
        </Section>

        {/* ------------------------------------------------ SECRETS */}
        <Section id="secrets" label={t("projectSettings.sections.secrets")} desc={t("projectSettings.secretsDesc")}>
          <Panel icon={KeyRound} title={t("projects.secrets.title")} sub={t("projects.secrets.hint")}>
            <ProjectSecrets projectId={projectId} bare />
          </Panel>
        </Section>

        {/* ------------------------------------------------ SHARING */}
        <Section id="sharing" label={t("projectSettings.sections.sharing")} desc={t("projectSettings.sharingDesc")}>
          <Panel icon={Users} title={t("projectSettings.access")} sub={t("projectSettings.membersHint")}>
            <ProjectMembers projectId={projectId} bare />
          </Panel>
        </Section>
      </div>
    </div>
  );
}

// ---------- layout atoms (the original's "section label + description, then dark cards") ----------

function Section({ id, label, desc, children }: { id: SectionId; label: string; desc: string; children: ReactNode }) {
  return (
    <section id={`project-${id}`} className="scroll-mt-28 space-y-3">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-fg-muted">{label}</div>
        <p className="mt-1 text-sm text-fg-muted">{desc}</p>
      </div>
      {children}
    </section>
  );
}

export function Panel({ icon: Icon, title, sub, badge, aside, children }: { icon: typeof Activity; title: ReactNode; sub?: ReactNode; badge?: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-surface">
      <header className="flex items-start gap-3 border-b border-border px-4 py-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg border border-border bg-surface-2 text-fg-muted"><Icon className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold">{title}</h3>{badge}</div>
          {sub && <div className="mt-0.5 text-xs text-fg-muted">{sub}</div>}
        </div>
        {aside && <div className="max-w-[40%] text-end text-[11px] text-fg-muted">{aside}</div>}
      </header>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Tile({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface-2/40 px-3 py-2">
      <div className="text-[11px] text-fg-muted">{label}</div>
      <div dir="ltr" className={cn("mt-0.5 truncate text-start text-sm", mono && "font-mono")}>{value}</div>
    </div>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded-md border border-border bg-surface-2/60 px-2 py-0.5 text-fg-muted">{children}</span>;
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-surface">
      <div className="border-b border-border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-fg-muted">{title}</div>
      <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface-2/40 px-3 py-2">
      <div className="text-[11px] text-fg-muted">{k}</div>
      <div dir="ltr" className={cn("mt-0.5 truncate text-start text-sm", mono && "font-mono")}>{v}</div>
    </div>
  );
}

function ToolingGroups({ health }: { health: ProjectHealth | undefined }) {
  const { t } = useTranslation();
  const v = (name: string) => health?.tools.find((x) => x.name === name)?.version ?? null;
  const providers: { key: AgentProvider; tool: string }[] = [{ key: "claude", tool: "claude" }, { key: "codex", tool: "codex" }, { key: "gemini", tool: "gemini" }, { key: "kimi", tool: "kimi" }];
  return (
    <>
      {providers.map(({ key, tool }) => (
        <Group key={key} title={`${PROVIDERS[key].label} · ${t("projectSettings.tooling")}`}>
          <KV k={t("projectSettings.cliInstalled")} v={health ? (v(tool) ? t("common.yes") : t("common.no")) : "—"} />
          <KV k={t("updates.version")} v={v(tool) ?? "—"} mono />
        </Group>
      ))}
      <Group title={t("projectSettings.devTooling")}>
        {["node", "npm", "pnpm", "python", "git", "gh", "code_server", "chromium"].map((n) => <KV key={n} k={n} v={v(n) ?? <span className="text-fg-muted">{t("health.missing")}</span>} mono />)}
      </Group>
    </>
  );
}

function AuthFiles({ health, projectAccountId }: { health: ProjectHealth | undefined; projectAccountId: string | null }) {
  const { t } = useTranslation();
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list, staleTime: 60_000 });
  const dirs: Record<AgentProvider, string> = { claude: "/home/dev/.claude-account", codex: "/home/dev/.codex", gemini: "/home/dev/.gemini", kimi: "/home/dev/.kimi" };
  return (
    <div className="rounded-xl border border-border bg-surface">
      <div className="border-b border-border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-fg-muted">{t("health.auth")}</div>
      <div className="space-y-2 p-3">
        {(Object.keys(PROVIDERS) as AgentProvider[]).map((prov) => {
          const acct = (accounts.data ?? []).find((a) => a.id === projectAccountId && a.provider === prov) ?? (accounts.data ?? []).find((a) => a.provider === prov && a.status === "connected");
          const mounted = prov === "claude" ? health?.auth.accountMounted : undefined;
          const present = prov === "claude" ? health?.auth.credentialsPresent : undefined;
          return (
            <div key={prov} className="rounded-lg border border-border p-3">
              <div className="mb-1 flex items-center gap-1.5 text-sm font-medium"><ProviderIcon provider={prov} size="xs" /> {PROVIDERS[prov].label}</div>
              <div className="rounded-md border border-border bg-surface-2/40 px-3 py-2">
                <div dir="ltr" className="break-all font-mono text-xs">{dirs[prov]}</div>
                <div className="mt-0.5 text-xs text-fg-muted">
                  {acct ? `${t("projectSettings.account")}: ${acct.label} · ${t(`accounts.status.${acct.status}`)}` : t("projectSettings.noAccountFor")}
                  {mounted !== undefined && ` · ${t("health.accountMounted")}: ${mounted ? t("common.yes") : t("common.no")}`}
                  {present !== undefined && ` · ${t("health.credentials")}: ${present ? t("common.yes") : t("common.no")}`}
                </div>
              </div>
            </div>
          );
        })}
        <p className="text-[11px] text-fg-muted">{t("health.authHint")}</p>
      </div>
    </div>
  );
}

function LimitsPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const p = useQuery(projectQuery(projectId)).data!;
  const health = useQuery({ queryKey: ["projects", projectId, "health"], queryFn: () => projectsApi.health(projectId), enabled: false });
  const docker = useQuery({ queryKey: ["system", "docker"], queryFn: projectsApi.dockerHealth, staleTime: 60_000 });
  const [form, setForm] = useState({ cpus: p.limits.cpus, memoryMb: p.limits.memoryMb, pids: p.limits.pids, idleStopMinutes: p.idleStopMinutes, name: p.name, description: p.description });
  const save = useMutation({
    mutationFn: () => projectsApi.update(projectId, { name: form.name, description: form.description, limits: { cpus: form.cpus, memoryMb: form.memoryMb, pids: form.pids }, idleStopMinutes: form.idleStopMinutes }),
    onSuccess: (np) => { qc.setQueryData(["projects", projectId], np); void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
  void docker;
  // "no limits" = 0 in all three; switching it off restores the last values (or the defaults)
  const unlimited = form.cpus === 0 && form.memoryMb === 0 && form.pids === 0;
  const [lastLimits, setLastLimits] = useState(() => (isUnlimited(p.limits) ? { cpus: 2, memoryMb: 2048, pids: 512 } : { cpus: p.limits.cpus, memoryMb: p.limits.memoryMb, pids: p.limits.pids }));
  const setUnlimited = (on: boolean) => {
    if (on) { setLastLimits({ cpus: form.cpus, memoryMb: form.memoryMb, pids: form.pids }); setForm({ ...form, cpus: 0, memoryMb: 0, pids: 0 }); }
    else setForm({ ...form, ...lastLimits });
  };
  const show = (v: number, text: string) => (v === 0 ? t("projectSettings.unlimitedValue") : text);
  return (
    <Panel icon={Settings2} title={t("projects.limits.title")} sub={t("projectSettings.limitsSub")}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <Tile label={t("projectSettings.effectiveCpu")} value={show(p.limits.cpus, `${p.limits.cpus} vCPU`)} />
          <Tile label={t("projectSettings.effectiveMemory")} value={show(p.limits.memoryMb, fmtMb(p.limits.memoryMb))} />
          <Tile label={t("projects.limits.pids")} value={show(p.limits.pids, String(p.limits.pids))} />
          <Tile label={t("projectSettings.serverMemory")} value={fmtMb(health.data?.disks[0]?.totalMb ?? null)} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t("projects.name")}><Input required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label={t("projects.description")}><Input maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        </div>
        <label className={cn("flex cursor-pointer items-start gap-3 rounded-lg border p-3", unlimited ? "border-warn/60 bg-warn-soft/30" : "border-border")}>
          <input type="checkbox" className="mt-1 size-4 accent-[var(--color-accent)]" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
          <span className="min-w-0">
            <span className="block font-medium">{t("projectSettings.unlimited")}</span>
            <span className="block text-xs text-fg-muted">{t("projectSettings.unlimitedHint")}</span>
          </span>
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          <Field label={t("projects.limits.cpus")} hint={unlimited ? t("projectSettings.unlimitedValue") : "0.25 – 256"}><Input dir="ltr" type="number" step="0.25" min={0.25} max={256} disabled={unlimited} value={unlimited ? "" : form.cpus} onChange={(e) => setForm({ ...form, cpus: Number(e.target.value) })} /></Field>
          <Field label={t("projects.limits.memoryMb")} hint={unlimited ? t("projectSettings.unlimitedValue") : "256 – 4194304"}><Input dir="ltr" type="number" step="256" min={256} max={4194304} disabled={unlimited} value={unlimited ? "" : form.memoryMb} onChange={(e) => setForm({ ...form, memoryMb: Number(e.target.value) })} /></Field>
          <Field label={t("projects.limits.pids")} hint={unlimited ? t("projectSettings.unlimitedValue") : "64 – 1000000"}><Input dir="ltr" type="number" step="64" min={64} max={1000000} disabled={unlimited} value={unlimited ? "" : form.pids} onChange={(e) => setForm({ ...form, pids: Number(e.target.value) })} /></Field>
          <Field label={t("projects.idleStop")} hint={t("projectSettings.idleShort")}><Input dir="ltr" type="number" step="5" min={0} max={10080} value={form.idleStopMinutes} onChange={(e) => setForm({ ...form, idleStopMinutes: Number(e.target.value) })} /></Field>
        </div>
        <Alert tone="warn">{t("projectSettings.limitsNote")}</Alert>
        {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setForm({ ...form, cpus: 2, memoryMb: 2048, pids: 512 })}><RotateCcw className="size-3.5" /> {t("projectSettings.defaults")}</Button>
          <Button type="submit" loading={save.isPending}>{t("projectSettings.saveLimits")}</Button>
        </div>
      </form>
    </Panel>
  );
}

/** Developer mode: sudo and the usual container capabilities, so agents work like on the user's own computer. */
function DevModePanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const p = useQuery(projectQuery(projectId)).data!;
  const set = useMutation({ mutationFn: (devMode: boolean) => projectsApi.update(projectId, { devMode }), onSuccess: (np) => qc.setQueryData(["projects", projectId], np) });
  const apply = useMutation({ mutationFn: () => projectsApi.recreate(projectId), onSuccess: (np) => { qc.setQueryData(["projects", projectId], np); void qc.invalidateQueries({ queryKey: ["projects"] }); } });
  const pending = p.stateMessage?.endsWith("pending_restart") && p.state === "running";
  return (
    <Panel icon={Terminal} title={t("projectSettings.devMode.title")} sub={t("projectSettings.devMode.sub")}>
      <label className={cn("flex cursor-pointer items-start gap-3 rounded-lg border p-3", p.devMode ? "border-warn/60 bg-warn-soft/30" : "border-border")}>
        <input type="checkbox" className="mt-1 size-4 accent-[var(--color-accent)]" checked={p.devMode} disabled={set.isPending} onChange={(e) => set.mutate(e.target.checked)} />
        <span className="min-w-0">
          <span className="block font-medium">{t("projectSettings.devMode.toggle")}</span>
          <span className="block text-xs text-fg-muted">{t("projectSettings.devMode.hint")}</span>
        </span>
      </label>
      {p.devMode && <Alert tone="warn" className="mt-3">{t("projectSettings.devMode.warn")}</Alert>}
      {pending && (
        <Alert tone="info" className="mt-3 flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1">{t("projectSettings.devMode.pending")}</span>
          <Button size="sm" loading={apply.isPending} onClick={() => apply.mutate()}><RefreshCw className="size-3.5" /> {t("projectSettings.devMode.apply")}</Button>
        </Alert>
      )}
      {(set.error || apply.error) && <Alert tone="danger" className="mt-3">{toMsg(set.error ?? apply.error)}</Alert>}
    </Panel>
  );
}

function AccountPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const p = useQuery(projectQuery(projectId)).data!;
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list });
  const setAccount = useMutation({ mutationFn: (agentAccountId: string | null) => projectsApi.update(projectId, { agentAccountId }), onSuccess: (np) => { qc.setQueryData(["projects", projectId], np); void qc.invalidateQueries({ queryKey: ["accounts"] }); } });
  const setMemory = useMutation({ mutationFn: (memoryDefault: boolean) => projectsApi.update(projectId, { memoryDefault }), onSuccess: (np) => qc.setQueryData(["projects", projectId], np) });
  return (
    <Panel icon={KeyRound} title={t("projectSettings.agents")} sub={t("projects.account.hint")}>
      {setAccount.error && <Alert tone="danger" className="mb-3">{toMsg(setAccount.error)}</Alert>}
      <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Field label={t("projectSettings.accountOverride")}>
          <Select value={p.agentAccountId ?? ""} disabled={setAccount.isPending || p.myRole !== "owner"} onChange={(e) => setAccount.mutate(e.target.value || null)}>
            <option value="">{t("projects.account.auto")}</option>
            {(accounts.data ?? []).map((a) => <option key={a.id} value={a.id}>{PROVIDERS[a.provider].label} · {a.label} · {t(`accounts.status.${a.status}`)}</option>)}
          </Select>
        </Field>
        <Link to="/settings" hash="settings-agents" className="text-sm text-accent underline">{t("projects.account.manage")}</Link>
      </div>
      <label className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
        <span>
          <span className="block font-medium">{t("memory.defaultOn")}</span>
          <span className="block text-xs text-fg-muted">{t("memory.defaultHint")}</span>
        </span>
        <input type="checkbox" className="size-4 accent-accent" checked={p.memoryDefault} disabled={p.myRole !== "owner"} onChange={(e) => setMemory.mutate(e.target.checked)} />
      </label>
    </Panel>
  );
}

function ImportPanel({ projectId, running }: { projectId: string; running: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [importing, setImporting] = useState<ImportSource | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <Panel icon={FolderUp} title={t("import.title")} sub={t("import.settingsHint")}>
      {msg && <Alert tone="success" className="mb-3">{msg}</Alert>}
      {!running && <Alert tone="warn" className="mb-3">{t("errors.project_not_running")}</Alert>}
      {importing ? (
        <ImportWizard projectId={projectId} source={importing} onDone={(m) => { setMsg(m); setImporting(null); void qc.invalidateQueries({ queryKey: ["projects", projectId] }); }} onCancel={() => setImporting(null)} />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled={!running} onClick={() => { setMsg(null); setImporting("upload"); }}>{t("create.upload")}</Button>
          <Button variant="secondary" disabled={!running} onClick={() => { setMsg(null); setImporting("github"); }}>{t("create.github")}</Button>
        </div>
      )}
    </Panel>
  );
}

function DrivePanel({ projectId, state }: { projectId: string; state: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const google = useQuery({ queryKey: ["google", "status"], queryFn: googleApi.status });
  const drive = useMutation({ mutationFn: () => googleApi.exportDrive(projectId) });
  return (
    <Panel icon={HardDriveDownload} title={t("drive.title")} sub={t("drive.hint")}>
      {drive.error && <Alert tone="danger" className="mb-3">{toMsg(drive.error)}</Alert>}
      {drive.data && <Alert tone="success" className="mb-3">{t("drive.done", { name: drive.data.name })} {drive.data.webViewLink && <a className="underline" href={drive.data.webViewLink} target="_blank" rel="noreferrer">{t("drive.open")}</a>}</Alert>}
      {google.data?.connected ? (
        <Button variant="secondary" loading={drive.isPending} disabled={state === "none" || state === "creating"} onClick={() => drive.mutate()}>{drive.isPending ? t("drive.exporting") : t("drive.export")}</Button>
      ) : (
        <Link to="/settings" hash="settings-agents" className="text-sm text-accent underline">{google.data && !google.data.configured ? t("drive.notConfigured") : t("drive.connectFirst")}</Link>
      )}
    </Panel>
  );
}

function DeleteButton({ projectId, name, onDeleted }: { projectId: string; name: string; onDeleted: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({ mutationFn: (purge: boolean) => projectsApi.remove(projectId, purge), onSuccess: onDeleted });
  if (!confirm) return <Button className="h-10 sm:col-span-2" variant="danger" onClick={() => setConfirm(true)}><Trash2 className="size-4" /> {t("projects.delete")}</Button>;
  return (
    <div className="space-y-2 sm:col-span-2">
      <Alert tone="danger">{t("projects.deleteConfirm", { name })}</Alert>
      {remove.error && <Alert tone="danger">{toMsg(remove.error)}</Alert>}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" loading={remove.isPending} onClick={() => remove.mutate(false)}>{t("projects.deleteKeepFiles")}</Button>
        <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(true)}>{t("projects.deletePurge")}</Button>
        <Button variant="ghost" onClick={() => setConfirm(false)}>{t("common.cancel")}</Button>
      </div>
    </div>
  );
}

